import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONCODINGS_LOAD,
    ACTION_INSTITUTIONCODINGS_LOAD_BY_ID,
    ACTION_INSTITUTIONCODINGS_LOAD_FAILURE,
    ACTION_INSTITUTIONCODINGS_LOAD_SUCCESS,

    ACTION_INSTITUTIONCODINGS_SAVE,
    ACTION_INSTITUTIONCODINGS_SAVE_SUCCESS,
    ACTION_INSTITUTIONCODINGS_SAVE_FAILURE,

    ACTION_INSTITUTIONCODINGS_CREATE,
    ACTION_INSTITUTIONCODINGS_CREATE_SUCCESS,
    ACTION_INSTITUTIONCODINGS_CREATE_FAILURE,

    ACTION_INSTITUTIONCODINGS_VALIDATE,
    ACTION_INSTITUTIONCODINGS_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONCODINGS_VALIDATE_FAILURE,

    ACTION_INSTITUTIONCODINGS_CLEAR,
    ACTION_INSTITUTIONCODINGS_FILTER,

    InstitutionCodingAction
} from '../actions/institutionCoding';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionCodingState {
    formData: Dtos.InstitutionCoding | Dtos.InstitutionCoding[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionCoding[];
    formLength: number;
}

const initialState: IInstitutionCodingState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionCodingState, _isLoading: boolean): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IInstitutionCodingState,
    formData: Dtos.InstitutionCoding | Dtos.InstitutionCoding[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IInstitutionCodingState, formFilter: IFormFilter): IInstitutionCodingState {

    const items: Dtos.InstitutionCoding[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IInstitutionCodingState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IInstitutionCodingState, responseStatus: Dtos.ResponseStatus): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IInstitutionCodingState, propertiesToClear?: string[]): IInstitutionCodingState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IInstitutionCodingState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionCodingState, _isSaving: boolean): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionCodingState,
    formData: Dtos.InstitutionCoding | Dtos.InstitutionCoding[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionCodingState, responseStatus: Dtos.ResponseStatus): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionCodingState, _isCreating: boolean): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionCodingState,
    formData: Dtos.InstitutionCoding,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionCodingState, responseStatus: Dtos.ResponseStatus): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IInstitutionCodingState, _isValidating: boolean): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionCodingState,
    formData: Dtos.InstitutionCoding,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionCodingState, responseStatus: Dtos.ResponseStatus): IInstitutionCodingState {
    const newState: IInstitutionCodingState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const InstitutionCodingReducer: Reducer<IInstitutionCodingState> = (state: IInstitutionCodingState = initialState, action: InstitutionCodingAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONCODINGS_LOAD:
        case ACTION_INSTITUTIONCODINGS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONCODINGS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONCODINGS_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONCODINGS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONCODINGS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONCODINGS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONCODINGS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCODINGS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default InstitutionCodingReducer;